import { CheckCircle2, ChevronUp, Clock, Loader2, Plus, Sparkles, Trash2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { db } from '../services/db';
import { FeatureRequest } from '../types';
import { ConfirmationModal } from './ConfirmationModal';

interface FeatureWallProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

export const FeatureWall: React.FC<FeatureWallProps> = ({ isOpen, onClose, userId }) => {
  const [features, setFeatures] = useState<FeatureRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'suggested' | 'in_progress' | 'complete'>('all');
  
  // New Feature Form
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const loadFeatures = async () => {
    setIsLoading(true);
    try {
      const data = await db.getFeatureRequests(userId);
      console.log('DEBUG: Loaded features:', data);
      setFeatures(data);
    } catch (error) {
      console.error("Failed to load features:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadFeatures();
    }
  }, [isOpen]);

  const handleVote = async (featureId: string) => {
    // Optimistic update
    setFeatures(prev => prev.map(f => {
      if (f.id === featureId) {
        const hasVoted = !f.hasVoted;
        return {
          ...f,
          hasVoted,
          voteCount: (f.voteCount || 0) + (hasVoted ? 1 : -1)
        };
      }
      return f;
    }));

    try {
      // We need to know the *previous* state to decide whether to add or remove vote.
      // But since we just toggled it in the optimistic update, we can check the *new* state from the features list
      // OR simpler: check the feature from the list before update.
      // Actually, let's just find the feature in the current state (before setFeatures takes effect fully or use functional update correctly).
      
      // Better approach: pass the current state to the handler
      const feature = features.find(f => f.id === featureId);
      if (!feature) return;

      if (feature.hasVoted) {
          await db.removeVote(featureId, userId);
      } else {
          await db.voteFeature(featureId, userId);
      }
    } catch (error) {
      console.error("Failed to vote:", error);
      // Revert on error
      loadFeatures();
    }
  };

  const handleSubmit = async () => {
    if (!newTitle.trim() || !newDesc.trim()) return;

    setIsSubmitting(true);
    try {
        await db.submitFeatureRequest(userId, newTitle, newDesc);
        setShowForm(false);
        setNewTitle('');
        setNewDesc('');
        loadFeatures();
    } catch (error: any) {
        console.error("Failed to submit feature:", error);
        alert("Failed to submit feature.");
    } finally {
        setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (featureId: string) => {
    setDeleteId(featureId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    
    try {
      await db.deleteFeatureRequest(deleteId);
      setFeatures(prev => prev.filter(f => f.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      console.error("Failed to delete feature:", error);
      alert("Failed to delete feature.");
    }
  };

  const filteredFeatures = features.filter(f => filter === 'all' || f.status === filter);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-brand-dark/60 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
    >
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-xl animate-scale-in flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-brand-cream">
          <div className="flex items-center gap-2">
            <div className="bg-brand-dark p-2 rounded-xl">
              <Sparkles className="text-brand-yellow" size={20} />
            </div>
            <div>
              <h3 className="font-display font-extrabold text-xl text-brand-dark">Feature Wall 🧱</h3>
              <p className="text-xs text-brand-dark-light">Vote on what we build next!</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-black/5 rounded-full transition-colors"
          >
            <X size={20} className="text-brand-dark-light" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-100 bg-white overflow-x-auto">
          <div className="flex gap-2">
             {/* ... filters ... */}
             {(['all', 'suggested', 'in_progress', 'complete'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(prev => prev === status ? 'all' : status)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
                    filter === status 
                      ? 'bg-brand-dark text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status === 'all' ? 'All' : status.replace('_', ' ').toUpperCase()}
                </button>
              ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {/* New Suggestion Form */}
            {!showForm ? (
                <button 
                    onClick={() => setShowForm(true)}
                    className="w-full py-3 px-4 bg-white border-2 border-dashed border-gray-300 rounded-xl text-gray-500 font-medium hover:border-brand-dark hover:text-brand-dark transition-colors flex items-center justify-center gap-2"
                >
                    <Plus size={18} />
                    Suggest a Feature
                </button>
            ) : (
                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm animate-fade-in">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="font-bold text-sm text-brand-dark">New Suggestion</h4>
                        <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                            <X size={16} />
                        </button>
                    </div>
                    <input 
                        type="text"
                        placeholder="Feature Title"
                        value={newTitle}
                        onChange={e => setNewTitle(e.target.value)}
                        className="w-full p-2 mb-2 text-sm border border-gray-200 rounded-lg focus:border-brand-dark outline-none"
                    />
                    <textarea 
                        placeholder="Describe it..."
                        value={newDesc}
                        onChange={e => setNewDesc(e.target.value)}
                        className="w-full p-2 mb-3 text-sm border border-gray-200 rounded-lg focus:border-brand-dark outline-none"
                        rows={3}
                    />
                    <button 
                        onClick={handleSubmit}
                        disabled={!newTitle.trim() || !newDesc.trim()}
                        className="w-full py-2 bg-brand-dark text-white rounded-lg text-sm font-bold disabled:opacity-50 hover:bg-black transition-colors"
                    >
                        Submit
                    </button>
                </div>
            )}

            {/* List */}
            {isLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand-dark-light" /></div>
            ) : filteredFeatures.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in">
                    <div className="w-16 h-16 bg-brand-cream rounded-full flex items-center justify-center mb-4">
                        <Sparkles className="text-brand-dark-light" size={32} />
                    </div>
                    <h4 className="font-display font-extrabold text-brand-dark text-lg mb-1">No features yet</h4>
                    <p className="text-brand-dark-light text-sm max-w-xs mx-auto mb-4">
                        Be the first to suggest something awesome for the community!
                    </p>
                    <button 
                        onClick={() => setShowForm(true)} // Changed from setIsAdding(true)
                        className="text-brand-red font-bold text-sm hover:underline"
                    >
                        Make a suggestion
                    </button>
                </div>
            ) : (
                filteredFeatures.map(feature => (
                    <div key={feature.id} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                        <div className="flex justify-between items-start gap-3">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                        feature.status === 'complete' ? 'bg-green-100 text-green-700' :
                                        feature.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                        'bg-gray-100 text-gray-600'
                                    }`}>
                                        {feature.status.replace('_', ' ')}
                                    </span>
                                    {feature.user?.uid === userId && (
                                    <button 
                                        onClick={() => handleDeleteClick(feature.id)}
                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                        title="Delete Suggestion"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                )}
                                </div>
                                <h4 className="font-bold text-brand-dark text-sm">{feature.title}</h4>
                            </div>
                            <p className="text-sm text-brand-dark-light mt-1">{feature.description}</p>
                            <div className="mt-2 flex items-center gap-2">
                                <img 
                                    src={feature.user?.photoURL || 'https://via.placeholder.com/20'} 
                                    className="w-5 h-5 rounded-full bg-brand-cream"
                                    alt="user"
                                />
                                <span className="text-xs text-brand-dark-light font-medium">{feature.user?.displayName || 'Unknown'}</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <button 
                                    onClick={() => handleVote(feature.id)}
                                    data-testid={`vote-btn-${feature.id}`}
                                    className={`flex flex-col items-center justify-center w-10 h-10 rounded-xl transition-all active:scale-95 ${
                                        feature.hasVoted 
                                        ? 'bg-brand-dark text-white shadow-lg' 
                                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                    }`}
                                >
                                    <ChevronUp size={20} className={feature.hasVoted ? 'stroke-[3px]' : 'stroke-2'} />
                                </button>
                                <span className="text-xs font-bold text-brand-dark">{feature.voteCount || 0}</span>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
       
        <ConfirmationModal 
            isOpen={!!deleteId}
            title="Delete Suggestion?"
            message="Are you sure you want to remove this suggestion? This cannot be undone."
            confirmText="Delete"
            isDestructive={true}
            onConfirm={handleConfirmDelete}
            onCancel={() => setDeleteId(null)}
        />
      </div>
    </div>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
    switch (status) {
        case 'suggested':
            return <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md text-[10px] font-bold uppercase tracking-wide">Suggested</span>;
        case 'in_progress':
            return (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-md text-[10px] font-bold uppercase tracking-wide flex items-center gap-1">
                    <Clock size={10} /> In Progress
                </span>
            );
        case 'complete':
            return (
                <span className="px-2 py-0.5 bg-green-100 text-green-600 rounded-md text-[10px] font-bold uppercase tracking-wide flex items-center gap-1">
                    <CheckCircle2 size={10} /> Complete
                </span>
            );
        default:
            return null;
    }
};
